// ContactsScreen — tenant-level master contacts register.
//
// v0.39.0 — initial. Lists every contact the tenant has stored. Used
//           as the source for project team selection (ProjectContacts)
//           and v0.39.1's defect-assignee picker. Brett's framing:
//           "architect has contacts, contractor has contacts, set
//           globally to a subscription then selected for the project."
//
// Layout: search + trade filter on the top row, table-style list below,
// click a row to open the editor modal. New / Edit share the same
// modal (just toggles between POST and PATCH).

import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiSend } from '../api';
import { alertFailure } from '../utils/sessionEnd';
import { theme, card, input, btnPrimary, btnSecondary } from '../theme';
// v0.46.7 — Shared page title (standardised heading size).
import PageHeading from '../components/PageHeading';
// v0.39.0 — Free tier cap (10 contacts) drives the upgrade prompt when
//           the contractor builds out their subbie roster past the cap.
import { isFreeTier, FREE_LIMITS } from '../utils/tier';
import UpgradePrompt from '../components/UpgradePrompt';
// v0.39.2 — bulk import (vCard / CSV / native picker).
import ContactImportModal from '../components/ContactImportModal';

// Trades pulled from the existing palette so the filter matches the
// pin colour scheme operators already learned on PlanScreen.
import { tradeLabel, tradeColor } from '../utils/trades';
import { useActiveTrades } from '../utils/useTrades';

export default function ContactsScreen() {
  // 2026-08-14 — the tenant's own list (Settings -> Mark types), falling back
  // to the built-in trades. A hook so this re-renders when the catalogue loads.
  const TRADES = useActiveTrades();

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [tradeFilter, setTradeFilter] = useState('');
  const [editing, setEditing] = useState(null); // null | {} (new) | existing contact
  const [upgradeReason, setUpgradeReason] = useState(null);
  // v0.39.2 — bulk-import modal visibility.
  const [importOpen, setImportOpen] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const params = [];
      if (q.trim()) params.push(`q=${encodeURIComponent(q.trim())}`);
      if (tradeFilter) params.push(`trade=${encodeURIComponent(tradeFilter)}`);
      const url = `/api/contacts${params.length ? `?${params.join('&')}` : ''}`;
      const list = await apiGet(url);
      setContacts(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e?.message || String(e));
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tradeFilter]);

  // Debounced search-on-type.
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [q]);

  // Group by trade for visual scan. "Other" bucket catches unset.
  const grouped = useMemo(() => {
    const m = new Map();
    for (const c of contacts) {
      const key = c.trade || '_';
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(c);
    }
    return Array.from(m.entries()).sort((a, b) => {
      if (a[0] === '_') return 1;
      if (b[0] === '_') return -1;
      return a[0].localeCompare(b[0]);
    });
  }, [contacts]);

  function handleNew() {
    if (isFreeTier() && contacts.length >= 10) {
      // Bespoke 10-cap on Contacts. FREE_LIMITS doesn't carry one yet so
      // we hard-code here; v0.39.x can promote to FREE_LIMITS.contacts.
      setUpgradeReason('contacts');
      return;
    }
    setEditing({});
  }

  async function handleSave(form) {
    const id = editing && editing.id;
    if (id) {
      const updated = await apiSend('PATCH', `/api/contacts/${id}`, form);
      setContacts(prev => prev.map(c => (c.id === id ? updated : c)));
    } else {
      const created = await apiSend('POST', '/api/contacts', form);
      setContacts(prev => [...prev, created].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    }
    setEditing(null);
  }

  async function handleDelete(c) {
    if (!c?.id) return;
    if (!window.confirm(`Delete "${c.name}"? This also removes them from every project they're on.`)) return;
    try {
      await apiSend('DELETE', `/api/contacts/${c.id}`);
      setContacts(prev => prev.filter(x => x.id !== c.id));
      setEditing(null);
    } catch (e) {
      alertFailure('Delete failed: ' + (e?.message || String(e)), e);
    }
  }

  // v0.43.0 — Send a magic-link invite to the contact. Pro feature;
  //           Free tier surfaces UpgradePrompt instead of sending.
  async function handleSendInvite(c) {
    if (isFreeTier()) {
      setUpgradeReason('contactInvite');
      return;
    }
    if (!c?.email) {
      window.alert(`${c.name} has no email on file. Add one and save first.`);
      return;
    }
    if (!window.confirm(`Send a sign-in link to ${c.email}? They'll see all items assigned to them across every project.`)) return;
    try {
      const r = await apiSend('POST', '/api/contact-invite', { contactId: c.id });
      // 2026-09-15 — "fix all the bugs": this said "Invite sent" whatever came
      // back. Offline or in site mode apiSend only QUEUES the request, and nothing
      // has been emailed yet; say so. A send the server could not make throws
      // (the endpoint now answers 502), and lands in the catch below.
      if (r && r.queued) {
        window.alert(r.held
          ? `Site mode is on, so the invite to ${c.email} is saved on this device and will send when you tap Sync.`
          : `No connection right now, so the invite to ${c.email} is saved on this device and will send when it reconnects.`);
      } else if (r && r.sent === true) {
        window.alert(`Invite sent to ${c.email}.`);
      } else {
        window.alert(`The invite to ${c.email} was not confirmed as sent. Try again.`);
      }
    } catch (e) {
      alertFailure('Send failed: ' + (e?.message || String(e)), e);
    }
  }

  return (
    <div>
      {/* Header row: title left, search + filter + new button right */}
      <div className="ss-toolbar" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
        <div>
          {/* v0.45.20 — Revert v0.44.0 cosmetic "Companies" rename.
              Data model is individual contacts (name + email + phone
              + a company affiliation). Header back to Contacts. */}
          <PageHeading>Contacts</PageHeading>
          <div style={{ fontSize: 13, color: theme.textLight, marginTop: 4 }}>
            Your master list of people — clients, builders, subbies, consultants, suppliers. Pick which ones go on each project from here.
          </div>
        </div>
        <div className="ss-toolbar-actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            placeholder="Search name, email, ABN…"
            value={q}
            onChange={e => setQ(e.target.value)}
            style={{ ...input, width: 240, maxWidth: '100%' }}
          />
          <select
            value={tradeFilter}
            onChange={e => setTradeFilter(e.target.value)}
            style={{ ...input, width: 160, maxWidth: '100%' }}
          >
            <option value="">All trades</option>
            {TRADES.map(t => (
              <option key={t.key} value={t.key}>{t.label}</option>
            ))}
          </select>
          {/* v0.39.2 — bulk import. vCard + CSV + native picker. */}
          <button
            onClick={() => setImportOpen(true)}
            style={{ ...btnSecondary, fontSize: 13 }}
          >Import…</button>
          <button onClick={handleNew} style={btnPrimary}>+ New contact</button>
        </div>
      </div>

      {error && (
        <div style={{ padding: 12, background: theme.bg, border: `1px solid ${theme.danger}`, color: theme.danger, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ ...card, textAlign: 'center', color: theme.textLight, padding: 40 }}>Loading…</div>
      ) : contacts.length === 0 ? (
        <EmptyState onCreate={handleNew} hasFilter={!!(q || tradeFilter)} />
      ) : (
        <div style={{ display: 'grid', gap: 14 }}>
          {grouped.map(([trade, items]) => (
            <div key={trade}>
              <div style={{
                fontSize: 11, fontWeight: 800, color: theme.textLight,
                textTransform: 'uppercase', letterSpacing: '0.05em',
                marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8,
              }}>
                {trade === '_' ? (
                  <>No trade <span style={{ color: theme.border }}>·</span> <span style={{ color: theme.textLight }}>{items.length}</span></>
                ) : (
                  <>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: tradeColor(trade), display: 'inline-block' }} />
                    {tradeLabel(trade)} <span style={{ color: theme.border }}>·</span> <span style={{ color: theme.textLight }}>{items.length}</span>
                  </>
                )}
              </div>
              <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
                {items.map((c, i) => (
                  <div
                    key={c.id}
                    onClick={() => setEditing(c)}
                    className="ss-contact-row"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1.4fr 1.6fr 1.4fr 1.2fr',
                      gap: 8,
                      padding: '10px 12px',
                      borderBottom: i < items.length - 1 ? `1px solid ${theme.border}` : 'none',
                      cursor: 'pointer',
                      fontSize: 13,
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ fontWeight: 600, color: theme.text }}>{c.name}</div>
                    <div style={{ color: theme.textLight }}>{c.email || ''}</div>
                    <div style={{ color: theme.textLight }}>{c.phone || ''}</div>
                    <div style={{ color: theme.textLight }}>{c.company || ''}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ContactEditModal
          contact={editing}
          onCancel={() => setEditing(null)}
          onSave={handleSave}
          onDelete={editing.id ? () => handleDelete(editing) : null}
          onSendInvite={editing.id ? () => handleSendInvite(editing) : null}
        />
      )}

      <UpgradePrompt
        open={!!upgradeReason}
        reason={upgradeReason}
        onClose={() => setUpgradeReason(null)}
      />

      {/* v0.39.2 — bulk import. Free-tier cap is enforced inside the modal
          (it shows how many will land vs how many were parsed) and the
          UpgradePrompt fires here if a partial import truncated. */}
      {importOpen && (
        <ContactImportModal
          existingCount={contacts.length}
          freeCap={isFreeTier() ? 10 : Infinity}
          onCancel={() => { setImportOpen(false); load(); }}
          onImported={({ truncated }) => {
            load();
            if (truncated) setUpgradeReason('contacts');
          }}
        />
      )}
    </div>
  );
}

function EmptyState({ onCreate, hasFilter }) {
  return (
    <div style={{ ...card, textAlign: 'center', padding: 40 }}>
      <div style={{ fontSize: 28, marginBottom: 6 }}>📇</div>
      <h2 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 6px' }}>
        {hasFilter ? 'No contacts match your filter' : 'No contacts yet'}
      </h2>
      <div style={{ fontSize: 13, color: theme.textLight, maxWidth: 420, margin: '0 auto' }}>
        {hasFilter
          ? 'Try clearing the search box or trade filter.'
          : 'Add the people you work with — clients, builders, subbies, consultants, suppliers. Once they\'re here you can pick which ones to put on each project.'}
      </div>
      {!hasFilter && (
        <button onClick={onCreate} style={{ ...btnPrimary, marginTop: 16 }}>+ New contact</button>
      )}
    </div>
  );
}

// v0.44.0 — Kind values. 'subcontractor' is the most common default.
const KIND_OPTIONS = [
  { value: 'client',        label: 'Client' },
  { value: 'builder',       label: 'Builder' },
  { value: 'subcontractor', label: 'Subcontractor' },
  { value: 'consultant',    label: 'Consultant' },
  { value: 'supplier',      label: 'Supplier' },
  { value: 'staff',         label: 'Staff' },
  { value: 'other',         label: 'Other' },
];

function ContactEditModal({ contact, onCancel, onSave, onDelete, onSendInvite }) {
  const [form, setForm] = useState({
    name:    contact.name    || '',
    kind:    contact.kind    || (contact.id ? '' : 'subcontractor'),
    email:   contact.email   || '',
    phone:   contact.phone   || '',
    trade:   contact.trade   || '',
    notes:   contact.notes   || '',
    // v0.45.20 — Restored: people belong to a company (free-text).
    company: contact.company || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  function patch(k, v) { setForm(prev => ({ ...prev, [k]: v })); }

  async function submit(e) {
    e?.preventDefault?.();
    if (!form.name.trim()) {
      setErr('Name is required.');
      return;
    }
    setErr(null); setSaving(true);
    try {
      await onSave({
        name:    form.name.trim(),
        kind:    form.kind || null,
        email:   form.email.trim() || null,
        phone:   form.phone.trim() || null,
        trade:   form.trade || null,
        notes:   form.notes.trim() || null,
        // v0.306.0 — the Company box was shown but never sent, so it never saved.
        company: form.company.trim() || null,
      });
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      onClick={() => !saving && onCancel()}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 100, padding: 16,
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        style={{
          background: theme.surface, width: 520, maxWidth: '100%',
          borderRadius: 10, padding: 22,
          boxShadow: '0 12px 32px rgba(15,23,42,0.25)',
        }}
      >
        <h3 style={{ margin: '0 0 14px', fontSize: 18, fontWeight: 800 }}>
          {contact.id ? 'Edit contact' : 'New contact'}
        </h3>

        {/* v0.45.20 — Reverted to per-person fields. Name = the
            person's name; Company = the org they work for. Kind
            classifies the relationship (client/builder/subbie/etc).
            Trade only meaningful for subcontractors. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Name" required full>
            <input value={form.name} onChange={e => patch('name', e.target.value)} style={input} autoFocus maxLength={200} placeholder="e.g. Brett Taylor" />
          </Field>
          <Field label="Company" full>
            <input value={form.company || ''} onChange={e => patch('company', e.target.value)} style={input} maxLength={200} placeholder="e.g. Insite Building Group" />
          </Field>
          <Field label="Kind">
            <select value={form.kind || ''} onChange={e => patch('kind', e.target.value)} style={input}>
              <option value="">— None —</option>
              {KIND_OPTIONS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
          </Field>
          <Field label="Trade">
            <select value={form.trade} onChange={e => patch('trade', e.target.value)} style={input} disabled={form.kind && form.kind !== 'subcontractor'}>
              <option value="">— None —</option>
              {TRADES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Email">
            <input type="email" value={form.email} onChange={e => patch('email', e.target.value)} style={input} maxLength={200} />
          </Field>
          <Field label="Phone">
            <input value={form.phone} onChange={e => patch('phone', e.target.value)} style={input} maxLength={60} />
          </Field>
          <Field label="Notes" full>
            <textarea value={form.notes} onChange={e => patch('notes', e.target.value)} style={{ ...input, height: 70, resize: 'vertical' }} />
          </Field>
        </div>

        {err && (
          <div style={{ marginTop: 10, padding: '8px 10px', background: theme.bg, border: `1px solid ${theme.danger}`, color: theme.danger, borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
            {err}
          </div>
        )}

        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              disabled={saving}
              style={{
                fontSize: 13, padding: '7px 12px',
                border: 0, background: 'transparent',
                color: theme.danger, cursor: saving ? 'default' : 'pointer',
                fontWeight: 600, opacity: saving ? 0.5 : 1,
              }}
            >Delete</button>
          )}
          {/* v0.43.0 — Send magic-link invite (Pro feature). */}
          {onSendInvite && (
            <button
              type="button"
              onClick={onSendInvite}
              disabled={saving}
              style={{
                fontSize: 13, padding: '7px 12px',
                border: 0, background: 'transparent',
                color: theme.brand, cursor: saving ? 'default' : 'pointer',
                fontWeight: 700, opacity: saving ? 0.5 : 1,
              }}
            >Send invite</button>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button type="button" onClick={onCancel} disabled={saving} style={btnSecondary}>Cancel</button>
            <button type="submit" disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving…' : (contact.id ? 'Save' : 'Create')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, required, full }) {
  return (
    <label style={{ display: 'block', gridColumn: full ? '1 / -1' : undefined }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: theme.textLight, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
        {label}{required && <span style={{ color: theme.danger, marginLeft: 4 }}>*</span>}
      </div>
      {children}
    </label>
  );
}
